import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { firestore } from '@/firebase'
import { CARE_PLAN_HUB_LIST_LIMIT } from '@gh/common/models'
import { useCustomerStore } from '@/stores/customer'

export type HubCarePlanRow = {
  consultationId: string
  scheduledStart: Date | null
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as Timestamp).toDate === 'function'
  ) {
    const parsed = (value as Timestamp).toDate()
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

export const useCustomerCarePlansStore = defineStore('customerCarePlans', () => {
  const customerStore = useCustomerStore()
  const carePlans = ref<HubCarePlanRow[]>([])
  const error = ref<string | null>(null)

  let unsubscribeQuery: (() => void) | null = null

  function teardown(): void {
    unsubscribeQuery?.()
    unsubscribeQuery = null
    carePlans.value = []
    error.value = null
  }

  function setupForCustomer(customerId: string): void {
    unsubscribeQuery?.()
    const carePlansQuery = query(
      collection(firestore, 'care_plans'),
      where('customerId', '==', customerId),
      where('status', '==', 'ready'),
      orderBy('scheduledStart', 'desc'),
      limit(CARE_PLAN_HUB_LIST_LIMIT),
    )
    unsubscribeQuery = onSnapshot(
      carePlansQuery,
      (snap) => {
        carePlans.value = snap.docs.map((docSnap) => {
          const data = docSnap.data()
          return {
            consultationId: docSnap.id,
            scheduledStart: toDate(data.scheduledStart),
          }
        })
        error.value = null
      },
      (err) => {
        error.value = err.message
        carePlans.value = []
      },
    )
  }

  watch(
    () => customerStore.customer?.id ?? null,
    (customerId) => {
      if (!customerId) {
        teardown()
        return
      }
      setupForCustomer(customerId)
    },
    { immediate: true },
  )

  const hasReadyCarePlans = computed(() => carePlans.value.length > 0)

  return {
    carePlans,
    error,
    hasReadyCarePlans,
    signOut: teardown,
  }
})
